import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, RadioGroup } from 'react-form';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import styles from './courtSchedular.scss';
import { CheckBox, Select, RadioButton } from '../../../components/Form';
import { required } from '../../../utils/validation';
import {
    allCourtsCheckboxKey,
    allowedTimePeriodIncrements,
    calculateSmartTimeIncrement,
    dateFormat,
    dateFormatForDayConfig,
    defaultEndTime,
    defaultStartTime,
    getDateTimeObjectFromCurrentDateAndTime,
    getDefaultFacilitiesSelected,
    getDefaultValuesFromConfigObject,
    getOptionsForCourtSchedulerTimeSelect,
    getSelectedCourtIds,
    hasInValidCourtSelection,
    validateEndTime,
    validateStartTime,
    FollowsByMatchStartOptions,
    FollowsByMatchStartOptionsList,
} from '../../../utils/courtSchedular';
import CourtDeskSidePanel from './CourtDeskSidePanel';
import { saveCourtConfigData } from '../../../actions/court-scheduler-actions';
import { getCourtSchedulerConfigForTheDay } from '../../../api/tmsEventsCourtSchedulerApi';
import { getApiAxiosInstance } from '../../../utils/network';
import Popup from '../../../components/Popup';

const CourtDeskConfig = (props) => {
    const {
        className,
        isEdit,
        closeEditCourtDeskConfig,
        activeFacilitiesAndCourts,
        eventId,
        eventSchedule,
    } = props;

    const dispatch = useDispatch();
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const drawsData = useSelector(({ drawEdit }) => drawEdit?.draws?.data);
    const sportTypeId = useSelector(({ drawEdit }) => drawEdit?.event?.data?.sportTypeId);

    const { selectedDate, defaultCourtConfig } = courtScheduler;
    const [currentDateConfig, setCurrentDateConfig] = useState(null);
    const [confirmCourtChangeData, setConfirmCourtChangeData] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const dateOfConfig = moment(selectedDate).tz(eventSchedule?.timeZone?.value).format(dateFormat);

    const defaultValues = useMemo(() => {
        if (!isEdit) {
            if (defaultCourtConfig) {
                return getDefaultValuesFromConfigObject(
                    defaultCourtConfig,
                    eventSchedule,
                    activeFacilitiesAndCourts,
                    selectedDate,
                );
            }

            if (!drawsData) {
                return null;
            }

            return {
                followsByMatchStartType: FollowsByMatchStartOptions.MANUAL,
                date: selectedDate,
                startTime: defaultStartTime,
                endTime: defaultEndTime,
                timeIncrements: calculateSmartTimeIncrement(drawsData, sportTypeId),
                facilities: getDefaultFacilitiesSelected(activeFacilitiesAndCourts),
            };
        }

        if (currentDateConfig) {
            return getDefaultValuesFromConfigObject(
                currentDateConfig,
                eventSchedule,
                activeFacilitiesAndCourts,
                selectedDate,
            );
        }

        return null;
    }, [selectedDate, currentDateConfig, defaultCourtConfig, activeFacilitiesAndCourts, eventSchedule, drawsData, sportTypeId]);

    const timeSelectOptions = useMemo(() => {
        return getOptionsForCourtSchedulerTimeSelect(eventSchedule?.timeZone?.value);
    }, [eventSchedule]);

    const endTimeSelectOptions = useMemo(() => {
        return getOptionsForCourtSchedulerTimeSelect(eventSchedule?.timeZone?.value, { includeEndOfDay: true });
    }, [eventSchedule]);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
    };

    useEffect(() => {
        const getDayConfig = async () => {
            const response = await getCourtSchedulerConfigForTheDay(getApiAxiosInstance(), eventId, {
                date: moment.utc(selectedDate).format(dateFormatForDayConfig),
            });
            setCurrentDateConfig(response.data);
        };

        if (isEdit && !currentDateConfig) {
            getDayConfig();
        }
    }, [isEdit, currentDateConfig, eventId, eventSchedule]);

    const hasMatchesScheduledForDay = currentDateConfig?.courts?.filter((court) => court.hasScheduledMatches)?.length > 0;

    const areDayDurationTimesChanged = (initialValues, values) => {
        const initialStartTime = moment(getDateTimeObjectFromCurrentDateAndTime(initialValues.date, initialValues.startTime, eventSchedule));
        const currentStartTime = moment(getDateTimeObjectFromCurrentDateAndTime(values.date, values.startTime, eventSchedule));

        const initialEndTime = moment(getDateTimeObjectFromCurrentDateAndTime(initialValues.date, initialValues.endTime, eventSchedule));
        const currentEndTime = moment(getDateTimeObjectFromCurrentDateAndTime(values.date, values.endTime, eventSchedule));

        return !initialStartTime.isSame(currentStartTime) || !initialEndTime.isSame(currentEndTime);
    };

    const submitButtonText = isEdit ? 'Save settings' : 'Set Courts';

    const onFormSubmit = async (values, checkCourtIdsChange = true) => {
        const updatedValues = {
            followsByMatchStartType: values.followsByMatchStartType,
            date: moment.utc(values.date).format(),
            startTime: getDateTimeObjectFromCurrentDateAndTime(values.date, values.startTime, eventSchedule),
            endTime: getDateTimeObjectFromCurrentDateAndTime(values.date, values.endTime, eventSchedule),
            timeIncrements: values.timeIncrements,
            courtIds: getSelectedCourtIds(values.facilities),
        };

        if (checkCourtIdsChange && currentDateConfig) {
            const allCourtsWithMatchesAreEnabled = currentDateConfig.courts.reduce((enabled, court) => {
                if (court.hasScheduledMatches) {
                    return enabled && updatedValues.courtIds.includes(court.courtId);
                }

                return enabled;
            }, true);

            if (!allCourtsWithMatchesAreEnabled) {
                setConfirmCourtChangeData(values);
                return;
            }
        }

        const shouldRefetchUnScheduleMatches = !checkCourtIdsChange;
        await dispatch(saveCourtConfigData(eventId, updatedValues, shouldRefetchUnScheduleMatches));

        if (isEdit) {
            closeEditCourtDeskConfig();
        }
    };

    const onConfirmFormSubmission = () => {
        onFormSubmit(confirmCourtChangeData, false);
        setConfirmCourtChangeData(null);
    };

    const onCloseFormSubmissionConfirm = () => {
        setConfirmCourtChangeData(null);
    };

    const onAllCourtsCheckChange = (formApi, facility) => (value) => {
        facility.courts.forEach((court) => {
            formApi.setValue(['facilities', facility.id, 'courts', court.id, 'selected'], value);
        });
    };

    const onCourtCheckChange = (formApi, facility, court) => (value) => {
        const selectedCourts = facility.courts.filter((courtObj) => {
            if (courtObj.id === court.id) {
                return value;
            }

            return formApi.getValue(['facilities', facility.id, 'courts', courtObj.id, 'selected']);
        });

        formApi.setValue(['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected'], selectedCourts.length === facility.courts.length);
    };

    if (!defaultValues) {
        return null;
    }

    return (
        <>
            <Form
                onSubmit={onFormSubmit}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm, getValue, errors, values } = formApi;

                    return (
                        <form onSubmit={submitForm} id="dayConfigForm" data-testid="draw-edit.court-config.form">
                            <CourtDeskSidePanel
                                className={className}
                                closeSideBar={handleClose}
                                isVisible={isVisible}
                                onClose={() => { closeEditCourtDeskConfig(); }}
                            >
                                <div className={styles.header} data-testid="draw-edit.court-config.header">
                                    {isEdit ? `Court settings for ${dateOfConfig}` : `Set courts for ${dateOfConfig}`}
                                </div>
                                <div className={styles.content}>
                                    <p className={`mb0 ${styles.notice}`} data-testid="draw-edit.court-config.intro-notice">
                                        Please select a facility and courts to create your court schedule grid
                                    </p>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.court-config.start-matches-title">
                                            Start matches
                                        </h6>
                                        <div className={styles.sectionDescription} data-testid="draw-edit.court-config.start-matches-description">
                                            How would you like to start Follows By matches?
                                        </div>
                                        <RadioGroup
                                            field="followsByMatchStartType"
                                        >
                                            {FollowsByMatchStartOptionsList.map(({ id, label }) => (
                                                <RadioButton
                                                    key={`followsByMatchStartType-${id}`}
                                                    id={`followsByMatchStartType-${id}`}
                                                    label={label}
                                                    value={id}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.court-config.day-duration-title">
                                            Day Duration
                                        </h6>
                                        <div className="row">
                                            <Select
                                                id="startTime"
                                                field="startTime"
                                                formGroupCssClass="col-6 mb0"
                                                options={timeSelectOptions}
                                                attributeToShow="title"
                                                attributeToSave="value"
                                                label="Start time"
                                                hiddenDefault
                                                validate={validateStartTime(formApi, selectedDate, eventSchedule)}
                                                themeV3
                                                data-testid="court-scheduler.court-config.start-time-select"
                                            />
                                            <Select
                                                id="endTime"
                                                field="endTime"
                                                formGroupCssClass="col-6 mb0"
                                                options={endTimeSelectOptions}
                                                attributeToShow="title"
                                                attributeToSave="value"
                                                label="End time"
                                                hiddenDefault
                                                validate={validateEndTime(formApi, selectedDate, eventSchedule)}
                                                themeV3
                                                data-testid="court-scheduler.court-config.end-time-select"
                                            />
                                        </div>
                                        {hasMatchesScheduledForDay && areDayDurationTimesChanged(defaultValues, values) && (
                                            <p className={`${styles.notice}`} data-testid="draw-edit.court-config.day-duration-shift-notice">
                                                <span className={styles.highlight} data-testid="draw-edit.court-config.day-duration-shift-notice.label">Note:</span> You have set different day duration times. All existing matches will auto shift to adhere to the new start time. Note: some matches may shift beyond the established end time.
                                            </p>
                                        )}
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.court-config.time-increments-title">
                                            Time Period Increments
                                        </h6>
                                        <Select
                                            id="timeIncrements"
                                            field="timeIncrements"
                                            options={allowedTimePeriodIncrements}
                                            validate={required}
                                            hiddenLabel
                                            hiddenDefault
                                            themeV3
                                            data-testid="court-scheduler.court-config.time-increments-select"
                                        />
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.court-config.facility-title">
                                            Facility
                                        </h6>
                                        {activeFacilitiesAndCourts.length > 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="draw-edit.court-config.facility-multi-description">
                                                    Which facilitie(s) would you like to use?
                                                </p>
                                                {activeFacilitiesAndCourts.map((facility) => {
                                                    return (
                                                        <div
                                                            key={`facility-${facility.id}`}
                                                            className={styles.facility}
                                                        >
                                                            <CheckBox
                                                                id={`facilities${facility.id}selected`}
                                                                field={['facilities', facility.id, 'selected']}
                                                                customLabel={facility.name}
                                                                formGroupCssClass={styles.checkbox}
                                                                data-testid={`court-scheduler.court-config.facility-${facility.id}-checkbox`}
                                                            />
                                                            {getValue(['facilities', facility.id, 'selected']) && (
                                                                <>
                                                                    {facility.courts.length > 1 && (
                                                                        <CheckBox
                                                                            id={`facilities${facility.id}${allCourtsCheckboxKey}selected`}
                                                                            field={['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                                            customLabel="All Courts"
                                                                            formGroupCssClass={`${styles.checkbox} ml32`}
                                                                            onChange={onAllCourtsCheckChange(formApi, facility)}
                                                                            data-testid={`court-scheduler.court-config.facility-${facility.id}-all-courts-checkbox`}
                                                                        />
                                                                    )}
                                                                    {facility.courts.map((court) => {
                                                                        return (
                                                                            <CheckBox
                                                                                key={`facility-${facility.id}-court-${court.id}`}
                                                                                id={`facilities${facility.id}${court.id}`}
                                                                                field={['facilities', facility.id, 'courts', court.id, 'selected']}
                                                                                customLabel={court.name}
                                                                                formGroupCssClass={`${styles.checkbox} ml32`}
                                                                                onChange={onCourtCheckChange(formApi, facility, court)}
                                                                                data-testid={`court-scheduler.court-config.facility-${facility.id}-court-${court.id}-checkbox`}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {activeFacilitiesAndCourts.length === 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="draw-edit.court-config.facility-single-description">
                                                    Which court(s) would you like to use?
                                                </p>
                                                <h6 className={styles.sectionTitle} data-testid="draw-edit.court-config.facility-single-name">
                                                    {activeFacilitiesAndCourts[0].name}
                                                </h6>
                                                <div
                                                    className={styles.facility}
                                                >
                                                    {activeFacilitiesAndCourts[0].courts?.length > 1 && (
                                                        <CheckBox
                                                            id={`facilities${activeFacilitiesAndCourts[0].id}${allCourtsCheckboxKey}selected`}
                                                            field={['facilities', activeFacilitiesAndCourts[0].id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                            customLabel="All Courts"
                                                            formGroupCssClass={`${styles.checkbox}`}
                                                            onChange={onAllCourtsCheckChange(formApi, activeFacilitiesAndCourts[0])}
                                                            data-testid="court-scheduler.court-config.single-facility-all-courts-checkbox"
                                                        />
                                                    )}
                                                    {activeFacilitiesAndCourts[0].courts?.map((court) => {
                                                        return (
                                                            <CheckBox
                                                                key={`facility-${activeFacilitiesAndCourts[0].id}-court-${court.id}`}
                                                                id={`facilities${activeFacilitiesAndCourts[0].id}${court.id}`}
                                                                field={['facilities', activeFacilitiesAndCourts[0].id, 'courts', court.id, 'selected']}
                                                                customLabel={court.name}
                                                                formGroupCssClass={`${styles.checkbox}`}
                                                                onChange={onCourtCheckChange(formApi, activeFacilitiesAndCourts[0], court)}
                                                                data-testid={`court-scheduler.court-config.single-facility-court-${court.id}-checkbox`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={`${styles.footer} d-flex`}>
                                    <button
                                        className={`btn btn-default ${styles.submitButton} flex-1 mr8`}
                                        type="button"
                                        onClick={handleClose}
                                        data-testid="draw-edit.court-config.cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`btn btn-primary ${styles.submitButton} flex-1`}
                                        type="submit"
                                        disabled={courtScheduler.isSavingConfig || errors || hasInValidCourtSelection(values)}
                                        data-testid="draw-edit.court-config.submit-btn"
                                    >
                                        {courtScheduler.isSavingConfig && (<i className="fas fa-circle-notch fa-spin" />)} {submitButtonText}
                                    </button>
                                </div>
                            </CourtDeskSidePanel>
                        </form>
                    );
                }}
            </Form>
            {confirmCourtChangeData && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmCourtSelectionPopup}
                    onCloseClick={onCloseFormSubmissionConfirm}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                >
                    <p className={styles.description} data-testid="modal.court-config-confirm.description">
                        Matches are scheduled on the court you are unselecting for this day. If you confirm this change, all matches scheduled on that court will be removed from the schedule and must be re-scheduled
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseFormSubmissionConfirm}
                            data-testid="modal.court-config-confirm.cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmFormSubmission}
                            data-testid="modal.court-config-confirm.ok-btn"
                        >
                            OK
                        </button>
                    </div>
                </Popup>
            )}
        </>
    );
};

CourtDeskConfig.propTypes = {
    className: PropTypes.string,
    isEdit: PropTypes.bool,
    closeEditCourtDeskConfig: PropTypes.func,
    activeFacilitiesAndCourts: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        courts: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })),
    })).isRequired,
    eventId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.shape({
        timeZone: PropTypes.shape({
            value: PropTypes.string,
        }),
    }).isRequired,
};

CourtDeskConfig.defaultProps = {
    className: '',
    isEdit: false,
    closeEditCourtDeskConfig: () => {},
};

export default CourtDeskConfig;
